/**
 * /accept-invitation/$invitationId — WS-C.
 *
 * Public route (child of the root, NOT the authed layout) so an invited person
 * can reach it before signing in. Three states:
 *   - not signed in  → prompt to sign in / create an account (the email link is
 *     durable, so returning to it after auth completes the flow)
 *   - signed in      → accept → set the new org active → clear tenant-scoped
 *     react-query cache → land on the dashboard
 *   - error          → surfaced via toast (expired / cancelled / email mismatch,
 *     all enforced server-side by Better Auth)
 */
import { useNavigate, useParams } from '@tanstack/react-router';
import { Loader2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { Button, buttonVariants } from '@/components/ui/button';
import { authClient } from '@/lib/auth-client';
import { authUrlWithInvitation } from '@/lib/oauth';
import { queryClient } from '@/lib/query-client';
import { cn } from '@/lib/utils';

export function AcceptInvitationPage() {
  const params = useParams({ strict: false }) as { invitationId?: string };
  const invitationId = params.invitationId ?? '';
  const navigate = useNavigate();
  const { data: session, isPending } = authClient.useSession();
  const [accepting, setAccepting] = useState(false);

  async function accept() {
    if (!invitationId) return;
    setAccepting(true);
    const { data, error } = await authClient.organization.acceptInvitation({ invitationId });
    if (error) {
      toast.error(error.message ?? 'Could not accept invitation');
      setAccepting(false);
      return;
    }
    // Set the freshly joined org active, then clear tenant-scoped cache so no
    // prior-workspace data bleeds into the first render.
    const joined = data as unknown as {
      invitation?: { organizationId?: string };
      member?: { organizationId?: string };
    } | null;
    const orgId = joined?.invitation?.organizationId ?? joined?.member?.organizationId;
    if (orgId) await authClient.organization.setActive({ organizationId: orgId });
    queryClient.clear();
    toast.success('Invitation accepted');
    void navigate({ to: '/dashboard' });
  }

  return (
    <div className="flex min-h-screen items-center justify-center px-6">
      <div className="w-full max-w-md rounded-xl border border-border bg-background p-8 text-center shadow-sm">
        <h1 className="text-lg font-semibold">Workspace invitation</h1>

        {isPending ? (
          <div className="mt-6 flex justify-center">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : !invitationId ? (
          <p className="mt-3 text-sm text-muted-foreground">This invitation link is invalid.</p>
        ) : !session ? (
          <>
            <p className="mt-3 text-sm text-muted-foreground">
              Sign in or create an account to accept your invitation. Use the email address the
              invite was sent to.
            </p>
            {/* Plain anchors carrying ?invitation=<id> (the login↔signup footer
                links use the same idiom for in-flight OAuth grants): both pages
                read it back and return here once there is a session, so the
                invitation survives the auth detour instead of being dropped. */}
            <div className="mt-6 flex flex-col gap-2">
              <a
                href={authUrlWithInvitation('/login', invitationId)}
                className={cn(buttonVariants(), 'w-full')}
              >
                Sign in
              </a>
              <a
                href={authUrlWithInvitation('/signup', invitationId)}
                className={cn(buttonVariants({ variant: 'outline' }), 'w-full')}
              >
                Create account
              </a>
            </div>
          </>
        ) : (
          <>
            <p className="mt-3 text-sm text-muted-foreground">
              You&apos;re signed in as{' '}
              <span className="font-medium text-foreground">{session.user.email}</span>. Accept to
              join the workspace.
            </p>
            <Button className="mt-6 w-full" onClick={() => void accept()} disabled={accepting}>
              {accepting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Accept invitation'}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
